import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightLeft,
  Building2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MoreHorizontal,
  Pause,
  Play,
  RefreshCcw,
  Search,
  SearchX,
  Trash2,
  User,
  UserPlus,
} from "lucide-react";
import {
  errorMessage,
  listCampaignContacts,
  type CampaignContact,
  type ContactEmailStatus,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BulkActionsBar } from "@/components/ui/BulkActionsBar";
import { AddContactsWizard } from "@/components/email/AddContactsWizard";
import { ContactStatusBadge, Hint } from "@/components/email/EmailBadges";
import { CompanyCell, EmailCell, LeadCell } from "@/components/email/LeadCells";
import { LeadIssues } from "@/components/email/LeadIssues";
import { SelectAllBox } from "@/components/email/SelectAllBox";
import { StepProgress } from "@/components/email/StepProgress";
import { useDebounce } from "@/hooks/useDebounce";
import { CONTACT_STATUS, CONTACT_STATUS_ORDER, EVENT_LABEL, shortAgo } from "@/lib/email";
import { formatDateTime, formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/pages/email/CampaignWorkspace";

const ANY = "__any__";
const PAGE_SIZES = [50, 100, 200];
const ADDED_WITHIN: { value: string; label: string; days: number | null }[] = [
  { value: ANY, label: "Added: any time", days: null },
  { value: "7", label: "Added: last 7 days", days: 7 },
  { value: "30", label: "Added: last 30 days", days: 30 },
  { value: "90", label: "Added: last 90 days", days: 90 },
];

/**
 * The campaign's leads — the one place where people are added to it and
 * managed: search, status, bulk pause / resume / move / remove, each lead's
 * drawer. Adding uses the existing wizard (contacts or companies, review,
 * then add); the backend decides who may join and says why not.
 */
export default function LeadsTab() {
  const navigate = useNavigate();
  const { slug, campaign, meta, access, openLead, version, changed, actions } = useWorkspace();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [statuses, setStatuses] = useState<ContactEmailStatus[]>([]);
  const [businessStatus, setBusinessStatus] = useState<string>(ANY);
  const [step, setStep] = useState<string>(ANY);
  const [hasEmail, setHasEmail] = useState<string>(ANY);
  const [addedWithin, setAddedWithin] = useState<string>(ANY);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const [items, setItems] = useState<CampaignContact[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [wizardOpen, setWizardOpen] = useState(false);

  const filterKey = [debouncedSearch, statuses.join(","), businessStatus, step, hasEmail, addedWithin, includeRemoved, pageSize].join("|");
  useEffect(() => {
    setPage(1);
  }, [filterKey]);
  useEffect(() => {
    setSelected(new Set());
  }, [filterKey, page, version]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const days = ADDED_WITHIN.find((o) => o.value === addedWithin)?.days ?? null;
    try {
      const res = await listCampaignContacts(slug, {
        campaign_id: campaign.id,
        status: statuses,
        business_status: businessStatus === ANY ? [] : [businessStatus],
        step: step === ANY ? null : Number(step),
        has_email: hasEmail === ANY ? null : hasEmail === "yes",
        added_after: days ? new Date(Date.now() - days * 86_400_000).toISOString() : null,
        include_removed: includeRemoved || statuses.includes("EMAIL_REMOVED"),
        search: debouncedSearch.trim(),
        page,
        page_size: pageSize,
      });
      setItems(res.items);
      setTotal(res.total);
      setCounts(res.status_counts);
    } catch (err) {
      setError(errorMessage(err, "Leads could not be loaded."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterKey carries the filters
  }, [slug, campaign.id, filterKey, page]);

  useEffect(() => {
    void load();
  }, [load, reload, version]);

  const selectedRows = items.filter((row) => selected.has(row.id));
  const anyFilter =
    !!debouncedSearch.trim() ||
    statuses.length > 0 ||
    businessStatus !== ANY ||
    step !== ANY ||
    hasEmail !== ANY ||
    addedWithin !== ANY;
  const allCount = Object.entries(counts)
    .filter(([status]) => includeRemoved || status !== "EMAIL_REMOVED")
    .reduce((sum, [, n]) => sum + n, 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // A locked pilot's leads stay as they are: no action on them from here.
  const canBulk = (access.can_pause || access.can_enroll || access.can_remove) && !campaign.execution_lock;
  const allSelected = items.length > 0 && items.every((row) => selected.has(row.id));
  const someSelected = items.some((row) => selected.has(row.id));
  const canPauseSel = selectedRows.some((r) => ["EMAIL_READY", "EMAIL_QUEUED", "EMAIL_ACTIVE"].includes(r.status));
  const canResumeSel = selectedRows.some((r) => r.status === "EMAIL_PAUSED");
  const canEndSel = selectedRows.some((r) => r.status !== "EMAIL_REMOVED");
  const writable = campaign.status !== "ARCHIVED" && !campaign.provider_legacy && !campaign.execution_lock;
  const canAdd = access.can_enroll && writable;

  function toggleStatus(status: ContactEmailStatus) {
    setStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  }

  return (
    <div className="space-y-3">
      {/* Toolbar: search, filters, add. */}
      <div className="space-y-3 rounded-plane bg-white p-3.5 shadow-plane">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search a lead, a company or an email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border-slate-200 bg-slate-50/60 pl-9"
              aria-label="Search leads"
            />
          </div>
          <Select value={businessStatus} onValueChange={setBusinessStatus}>
            <SelectTrigger className="h-9 w-[150px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Business status">
              <SelectValue placeholder="Any business status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any business status</SelectItem>
              {meta.business_statuses.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={step} onValueChange={setStep} disabled={!campaign.steps.length}>
            <SelectTrigger className="h-9 w-[112px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Step">
              <SelectValue placeholder="Any step" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any step</SelectItem>
              {campaign.steps.map((s) => (
                <SelectItem key={s.position} value={String(s.position)}>
                  Sent step {s.position}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={hasEmail} onValueChange={setHasEmail}>
            <SelectTrigger className="h-9 w-[118px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Email present">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Email: any</SelectItem>
              <SelectItem value="yes">With email</SelectItem>
              <SelectItem value="no">Without email</SelectItem>
            </SelectContent>
          </Select>
          <Select value={addedWithin} onValueChange={setAddedWithin}>
            <SelectTrigger className="h-9 w-[146px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Date added">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ADDED_WITHIN.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canAdd && (
            <Button variant="primary" size="sm" className="ml-auto h-9" onClick={() => setWizardOpen(true)}>
              <UserPlus className="h-3.5 w-3.5" />
              Add leads
            </Button>
          )}
        </div>
        {/* Status chips with live counts. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setStatuses([])}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
              statuses.length === 0
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
            )}
          >
            All <span className="ml-0.5 tabular-nums opacity-70">{formatNumber(allCount)}</span>
          </button>
          {CONTACT_STATUS_ORDER.filter((s) => s !== "EMAIL_REMOVED" || includeRemoved || counts[s]).map((status) => {
            const on = statuses.includes(status);
            const tone = CONTACT_STATUS[status];
            const count = counts[status] ?? 0;
            return (
              <Hint key={status} content={tone.hint}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleStatus(status)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    on ? tone.pill + " ring-2 ring-slate-200 ring-offset-1" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                    !count && !on && "opacity-50",
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
                  {tone.label}
                  <span className="tabular-nums opacity-70">{formatNumber(count)}</span>
                </button>
              </Hint>
            );
          })}
          <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-500">
            <Switch checked={includeRemoved} onCheckedChange={setIncludeRemoved} />
            Show removed
          </label>
        </div>
      </div>

      {/* Table. */}
      <div className="overflow-hidden rounded-plane bg-white shadow-plane">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
          <p className="text-xs text-slate-500">
            {loading && !items.length ? "Loading…" : `${formatNumber(total)} ${total === 1 ? "lead" : "leads"}`}
          </p>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => setReload((n) => n + 1)}>
            <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
        {error ? (
          <div className="p-8 text-center text-sm text-rose-600">{error}</div>
        ) : loading && !items.length ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        ) : !items.length ? (
          anyFilter ? (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <SearchX className="h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">No lead matches these filters</p>
              <p className="mt-1 text-xs text-slate-500">Loosen a filter or clear the search.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500">
                <UserPlus className="h-5 w-5" />
              </span>
              <p className="mt-3 text-sm font-semibold text-slate-800">No lead in “{campaign.name}” yet</p>
              <p className="mt-1 max-w-sm text-xs text-slate-500">
                Pick contacts — or whole companies — from Prism. They start Ready: nothing is sent until you launch
                them from the Launch tab.
              </p>
              {canAdd && (
                <Button variant="primary" size="sm" className="mt-4" onClick={() => setWizardOpen(true)}>
                  <UserPlus className="h-3.5 w-3.5" />
                  Add leads
                </Button>
              )}
            </div>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  {canBulk && (
                    <th className="w-10 px-4 py-2">
                      <SelectAllBox
                        label="Select all"
                        all={allSelected}
                        some={someSelected}
                        onChange={(on) => setSelected(on ? new Set(items.map((r) => r.id)) : new Set())}
                      />
                    </th>
                  )}
                  {["Lead", "Company", "Email", "Status", "Step", "Issues", "Last activity", ""].map((label, i) => (
                    <th
                      key={label || i}
                      className={cn(
                        "whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400",
                        label === "Last activity" && "hidden xl:table-cell",
                      )}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => openLead(row.id)}
                    className={cn(
                      "group cursor-pointer border-b border-slate-50 align-top transition-colors last:border-0 hover:bg-indigo-50/30",
                      selected.has(row.id) && "bg-indigo-50/40",
                      row.status === "EMAIL_REMOVED" && "opacity-60",
                    )}
                  >
                    {canBulk && (
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${row.contact_name}`}
                          checked={selected.has(row.id)}
                          onCheckedChange={(checked) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(row.id);
                              else next.delete(row.id);
                              return next;
                            })
                          }
                          className="border-slate-300 data-[state=checked]:border-indigo-600 data-[state=checked]:bg-indigo-600"
                        />
                      </td>
                    )}
                    <td className="max-w-[200px] px-3 py-2">
                      <LeadCell row={row} />
                    </td>
                    <td className="max-w-[190px] px-3 py-2">
                      <CompanyCell row={row} />
                    </td>
                    <td className="max-w-[220px] px-3 py-2">
                      <EmailCell row={row} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <ContactStatusBadge status={row.status} reason={row.status_reason} />
                        {row.dry_run && (
                          <Hint content="Launched in a dry run: nothing was sent.">
                            <span className="rounded border border-amber-200 bg-amber-50 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                              Dry
                            </span>
                          </Hint>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <StepProgress current={row.current_step} total={row.steps_count} />
                    </td>
                    <td className="max-w-[240px] px-3 py-2">
                      <LeadIssues row={row} />
                    </td>
                    <td className="hidden whitespace-nowrap px-3 py-2 text-xs xl:table-cell">
                      {row.last_event_type ? (
                        <p className="text-slate-700" title={formatDateTime(row.last_event_at)}>
                          {EVENT_LABEL[row.last_event_type] ?? row.last_event_type}
                          <span className="text-slate-400"> · {shortAgo(row.last_event_at)}</span>
                        </p>
                      ) : (
                        <p className="text-slate-400">No event yet</p>
                      )}
                      <p className="text-[11px] text-slate-400" title={formatDateTime(row.added_at)}>
                        Added {shortAgo(row.added_at)}
                      </p>
                    </td>
                    <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 opacity-60 group-hover:opacity-100"
                            aria-label={`Actions for ${row.contact_name}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          {access.can_pause && row.status === "EMAIL_PAUSED" && (
                            <DropdownMenuItem onClick={() => void actions.act("resume", [row])}>
                              <Play className="h-4 w-4" /> Resume
                            </DropdownMenuItem>
                          )}
                          {access.can_pause && ["EMAIL_READY", "EMAIL_QUEUED", "EMAIL_ACTIVE"].includes(row.status) && (
                            <DropdownMenuItem onClick={() => void actions.act("pause", [row])}>
                              <Pause className="h-4 w-4" /> Pause
                            </DropdownMenuItem>
                          )}
                          {access.can_enroll && row.status !== "EMAIL_REMOVED" && (
                            <DropdownMenuItem onClick={() => void actions.move([row])}>
                              <ArrowRightLeft className="h-4 w-4" /> Change campaign
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate(`/projects/${row.project_slug}/contacts?contact=${row.contact_id}`)}>
                            <User className="h-4 w-4" /> Open contact
                          </DropdownMenuItem>
                          {row.company_id && (
                            <DropdownMenuItem onClick={() => navigate(`/projects/${row.project_slug}/companies/${row.company_id}`)}>
                              <Building2 className="h-4 w-4" /> Open company
                            </DropdownMenuItem>
                          )}
                          {row.provider_url && (
                            <DropdownMenuItem onClick={() => window.open(row.provider_url ?? "", "_blank", "noopener")}>
                              <ExternalLink className="h-4 w-4" /> Open in Lemlist
                            </DropdownMenuItem>
                          )}
                          {access.can_remove && row.status !== "EMAIL_REMOVED" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-rose-600 focus:text-rose-700" onClick={() => void actions.act("remove", [row])}>
                                <Trash2 className="h-4 w-4" /> Remove from campaign
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            <span>
              {formatNumber((page - 1) * pageSize + 1)}–{formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)}
            </span>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="h-7 w-[92px] rounded-md border-slate-200 bg-white text-xs" aria-label="Rows per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="tabular-nums">
                {page} / {totalPages}
              </span>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <BulkActionsBar count={selected.size} onClear={() => setSelected(new Set())} noun={selected.size === 1 ? "lead selected" : "leads selected"}>
        {access.can_pause && (
          <>
            <Button variant="outline" size="sm" disabled={actions.busy || !canPauseSel} onClick={() => void actions.act("pause", selectedRows)}>
              <Pause className="h-3.5 w-3.5" /> Pause
            </Button>
            <Button variant="outline" size="sm" disabled={actions.busy || !canResumeSel} onClick={() => void actions.act("resume", selectedRows)}>
              <Play className="h-3.5 w-3.5" /> Resume
            </Button>
          </>
        )}
        {access.can_enroll && (
          <Button variant="outline" size="sm" disabled={actions.busy || !canEndSel} onClick={() => void actions.move(selectedRows)}>
            <ArrowRightLeft className="h-3.5 w-3.5" /> Change campaign
          </Button>
        )}
        {access.can_remove && (
          <Button
            variant="outline"
            size="sm"
            className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            disabled={actions.busy || !canEndSel}
            onClick={() => void actions.act("remove", selectedRows)}
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </Button>
        )}
      </BulkActionsBar>

      {canAdd && (
        <AddContactsWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          slug={slug}
          meta={meta}
          campaigns={[campaign]}
          defaultCampaignId={campaign.id}
          onDone={changed}
        />
      )}
    </div>
  );
}
